import { IBidResponse, IAuctionResults } from '../../typings/IPrebid'
import { Bidder } from '../enums'
import EventManager from '../eventManager/EventManager'
import WebModel from '../models/WebModel'
import { Flooring } from '../flooring/Flooring'
import { Slot } from '../slots/Slot'
import { meetsOutstreamCriteria } from '../outstream/hasOutstream'
import { MediaType } from '../enums/MediaType'
import { VideoBidder } from '../enums/Bidder'
import { IVideoBidRestrictions } from '../../typings/IVideoOptions'
import { isNativeVideo } from '../native/helpers/isNativeVideo'

const TTL_OVERRIDE_VALUE = 60
const MAX_VIDEO_TTL = 600

export class AuctionHouse {
  private receivedBids: Array<IBidResponse> = []
  // private overrideTtls = []
  readonly ttlOverrideValue = TTL_OVERRIDE_VALUE

  constructor(private readonly model: WebModel) {}

  /**
   * Adds bid the the bidPool
   * @param bids
   */
  addBids(bids: Array<IBidResponse>): void {
    for (const bid of bids) {
      let floor

      // Extract the number from the adUnitCode.
      // e.g.: recipe_1_outstream_desktop => 1, sidebar_btf => 1, content_12_mobile => 12 etc.
      const numberMatches = bid.adUnitCode.match(/_(\d+)_/)
      const adUnitNumber = numberMatches ? parseInt(numberMatches[1]) : 1
      bid.number = adUnitNumber

      if (
        bid.adUnitCode.indexOf('_outstream') !== -1 ||
        bid.adUnitCode.indexOf('_ibv') !== -1
      ) {
        bid.isOutstream = true
        floor = Flooring.getOutstreamFloor(this.model)
        this.convertOutstreamBidsAdunits(bid)
      } else if (isNativeVideo(bid)) {
        floor = Flooring.getOutstreamFloor(this.model)
      } else if (bid.mediaType === MediaType.video) {
        floor = Flooring.getVideoFloor(this.model)

        // Reject video bids with invalid Vast Values
        if (!bidHasValidVastValue(bid)) {
          continue
        }
      } else {
        floor = Flooring.getDisplayFloor(this.model, bid.adUnitCode)
      }

      // No need to add bids of zero to the pool.
      if (bid.cpm >= floor) {
        // We suspect that the ttls for the bidders in overrideTtls
        // have their numbers set incorrectly, so we override them here
        /*
        if (this.overrideTtls.indexOf(bid.bidder) !== -1) {
          bid.ttl = this.ttlOverrideValue
        }
        */

        // We suspect that video bid caching doesn't last as long as the ttls on video bids.
        // So we're setting a maximum ttl of 10 minutes for video bids.
        if (bid.mediaType === MediaType.video) {
          const tenMins = 10 * 60
          bid.ttl = Math.min(bid.ttl, MAX_VIDEO_TTL)
        }

        bid.url = window.location.href
        this.receivedBids.push(bid)
      }
    }
  }

  /**
   * Outstream Bids have their own adUnitCodes, but we need to convert them so they
   * compete and appear along side the display ads
   * e.g.: recipe_2_outstream_desktop => recipe_2_btf ; content_outstream_mobile => content_mobile
   * sidebar_btf_outstream => sidebar_btf
   * @param bid
   */
  private convertOutstreamBidsAdunits(bid: IBidResponse): void {
    let outstreamAdunitId = bid.adUnitCode

    // convert 'desktop' to 'btf' to match our adunit naming convention
    outstreamAdunitId = outstreamAdunitId.replace('desktop', 'btf')
    outstreamAdunitId = outstreamAdunitId.replace('_1_', '_')
    outstreamAdunitId = outstreamAdunitId.replace('_outstream', '')
    outstreamAdunitId = outstreamAdunitId.replace('_ibv', '')

    bid.outstreamAdUnitCode = bid.adUnitCode
    bid.adUnitCode = outstreamAdunitId
  }

  getHighestBidData(slot: Slot, currentBid: IAuctionResults): IHighestBidData {
    const hbCount = this.getHbCountForSlot(slot)
    const highestBid = this.popHighestBidForSlot(slot)

    const currentBidWon =
      highestBid && highestBid.adId
        ? highestBid.adId !==
          (currentBid.targeting && currentBid.targeting.hb_adid)
        : false

    return {
      hbCount,
      highestBid,
      currentBidWon
    }
  }

  /**
   * Pass in a Slot, and this will find and remove from the bidPool
   * the highest bid that is valid for that adUnit.
   * @param slot
   */
  popHighestBidForSlot(
    slot: Slot,
    bidSpecificOptions: IVideoBidRestrictions = {}
  ): IBidResponse | null {
    const highestBid = this.getHighestBidForSlot(slot, bidSpecificOptions)
    removeFromArray(this.receivedBids, highestBid)

    return highestBid
  }

  /**
   * Returns a list of Bidders who have a bid in the pool thats is valid
   * for the passed in Slot
   * @param adUnitId
   */

  getBiddersForSlot(slot: Slot): Array<Bidder> {
    const bidders = {}

    this.getValidBidsForSlot(slot).forEach((bid) => {
      bidders[bid.bidder] = true
    })

    return Object.keys(bidders) as Array<Bidder>
  }

  getSize(): number {
    return this.receivedBids.length
  }

  /**
   * Calculates the hb_count for a particular Slot in the pool. 20 is max value returned.
   * @param slot
   */
  getHbCountForSlot(slot: Slot): number {
    const validBids = this.getValidBidsForSlot(slot)

    /* eslint-disable-next-line no-magic-numbers */
    const hbCount = validBids.length > 20 ? 20 : validBids.length

    return hbCount
  }

  private getValidBidsForSlot(
    slot: Slot,
    bidSpecificOptions: IVideoBidRestrictions = {}
  ): Array<IBidResponse> {
    this.cleanPool()
    const adUnitId = slot.id

    return this.receivedBids.filter((bid) => {
      // Only bids for the current URL are valid
      if (bid.url !== window.location.href) {
        return false
      }

      if (slot.sizes && bid.width && bid.height && slot.refreshSizeRestricted) {
        if (bid.isOutstream) {
          const slotMaxWidth = Math.max(
            ...slot.sizes.map((size) => (Array.isArray(size) && size[0]) || 0)
          )
          const slotMaxHeight = Math.max(
            ...slot.sizes.map((size) => (Array.isArray(size) && size[1]) || 0)
          )
          if (bid.height > slotMaxHeight) {
            return false
          }
        } else {
          const bidSize =
            bid.width <= 1 && bid.height <= 1
              ? 'fluid'
              : ([bid.width, bid.height] as AdSize)
          const adDimensionsAreValid = slot.sizes.reduce((isIncluded, size) => {
            if (isIncluded) {
              return true
            }

            if (typeof size !== typeof bidSize) {
              return false
            } else if (typeof size === 'string') {
              return size === bidSize
            } else {
              return size[0] === bidSize[0] && size[1] === bidSize[1]
            }
          }, false)

          if (!adDimensionsAreValid) {
            return false
          }
        }
      }

      // Remove the numbers from adunits like `content_3_btf` or
      // the '_refresh' from 'adhesion_desktop_refresh'
      const baseBidAdUnitId = bid.adUnitCode.replace(/_[0-9]+|_refresh/g, '')
      const baseAdUnitId = adUnitId.replace(/_[0-9]+|_refresh/g, '')

      if (baseBidAdUnitId !== baseAdUnitId) {
        return false
      }

      // Outstream bids are only valid in certain slots.
      if (isOutstreamBid(bid) && !meetsOutstreamCriteria(slot)) {
        return false
      }

      if (!bidMatchesSpecificOptions(bid, bidSpecificOptions)) {
        return false
      }

      return true
    })
  }

  /**
   * Pass in a Slot, and this will find the highest bid that is valid
   * @param slot
   */
  public getHighestBidForSlot(
    slot: Slot,
    bidSpecificOptions: IVideoBidRestrictions = {}
  ): IBidResponse | null {
    let highestBid: IBidResponse | null = null
    const validBids = this.getValidBidsForSlot(slot, bidSpecificOptions)
    validBids.forEach((bid) => {
      if (!highestBid) {
        highestBid = bid
      } else {
        if (bid.cpm > highestBid.cpm) {
          highestBid = bid
        }
      }
    })

    return highestBid
  }

  /**
   * Removes bids whose TTL have expired
   */
  private cleanPool(): void {
    const now = Date.now()
    // add a second to cutoff timestamp just top be safe.
    const oneSecondFromNow = now + 1000
    this.receivedBids = this.receivedBids.filter((bid) => {
      return bid.responseTimestamp + bid.ttl * 1000 > oneSecondFromNow
    })
  }
}

function removeFromArray<T>(array: Array<T>, element: T): Array<T> {
  const index = array.indexOf(element)

  if (index !== -1) {
    array.splice(index, 1)
  }

  return array
}

interface IHighestBidData {
  hbCount: number
  highestBid: IBidResponse | null
  currentBidWon: boolean
}

function isOutstreamBid(bid: IBidResponse): boolean {
  return !!bid.isOutstream
}

function bidHasValidVastValue(bidResponse: IBidResponse): boolean {
  return !!(bidResponse.vastUrl || bidResponse.vastXml)
}

function bidMatchesSpecificOptions(
  bid: IBidResponse,
  bidSpecificOptions: IVideoBidRestrictions
): boolean {
  if (
    bidSpecificOptions.startDelay !== undefined &&
    !bidMatchesStartDelay(bid, bidSpecificOptions.startDelay)
  ) {
    return false
  }

  if (
    bidSpecificOptions.adBreakType !== undefined &&
    !bidMatchesBreakType(bid, bidSpecificOptions.adBreakType)
  ) {
    return false
  }

  return true
}

function bidMatchesStartDelay(
  bid: IBidResponse,
  requestedStartdelay: number
): boolean {
  if (bid.bidRequest?.bidder === VideoBidder.indexExchange) {
    return startDelayCompatible(
      bid.bidRequest.params.video.startdelay,
      requestedStartdelay
    )
  } else if (bid.bidRequest?.bidder === VideoBidder.pubmatic) {
    return startDelayCompatible(
      bid.bidRequest.params.video.startdelay,
      requestedStartdelay
    )
  }

  return true
}

function bidMatchesBreakType(
  bid: IBidResponse,
  breakType: 'linear' | 'nonlinear'
): boolean {
  if (
    (bid.auctionId === MediaType.video && breakType === 'nonlinear') ||
    (bid.auctionId === MediaType.overlay && breakType === 'linear')
  ) {
    return false
  }

  return true
}

function startDelayCompatible(
  bidStartdelay: number,
  requestedStartdelay: number
): boolean {
  return !!bidStartdelay === !!requestedStartdelay
}
